import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message, Modal } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { get, post, setMerchantToken, setMerchantApps, getMerchantToken } from '../../utils/merchantApi';
import CryptoJS from 'crypto-js';
import '../../styles/login.css';

const GA_LENGTH = 6;

const MerchantLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [gaModalVisible, setGaModalVisible] = useState(false);
  const [loginTicket, setLoginTicket] = useState('');
  const [gaAccount, setGaAccount] = useState('');
  const [gaCodeArr, setGaCodeArr] = useState(Array(GA_LENGTH).fill(''));
  const [gaSubmitting, setGaSubmitting] = useState(false);
  const gaInputRefs = useRef([]);

  useEffect(() => {
    const token = getMerchantToken();
    if (token) {
      navigate('/merchant');
    }
  }, [navigate]);

  // 弹框打开时清空并聚焦第一格
  useEffect(() => {
    if (gaModalVisible) {
      setGaCodeArr(Array(GA_LENGTH).fill(''));
      setTimeout(() => gaInputRefs.current[0]?.focus(), 100);
    }
  }, [gaModalVisible]);

  // 6位填满自动提交
  useEffect(() => {
    if (gaModalVisible && gaCodeArr.every((d) => /^\d$/.test(d)) && !loading && !gaSubmitting) {
      handleGaSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gaCodeArr.join('')]);

  const encryptPassword = (password) => {
    return CryptoJS.SHA256(password).toString();
  };

  const handleLoginSuccess = async (token) => {
    setMerchantToken(token);

    const appsResponse = await get('/merchant/apps');
    if (appsResponse.data?.length > 0) {
      setMerchantApps(appsResponse.data);
      if (appsResponse.data.length === 1) {
        localStorage.setItem('currentAppId', appsResponse.data[0].app_id);
      }
    } else {
      message.error('您尚未绑定商户应用');
      return;
    }

    message.success('登录成功');
    navigate('/merchant');
  };

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await post('/app/auth/login', {
        loginType: 'password',
        phone: values.phone,
        password: encryptPassword(values.password)
      });

      if (response.data?.needGa) {
        setLoginTicket(response.data.loginTicket);
        setGaAccount(response.data.account);
        setGaModalVisible(true);
        return;
      }

      if (response.data?.token) {
        await handleLoginSuccess(response.data.token);
      }
    } catch (error) {
      console.error('登录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 单格输入
  const handleGaInputChange = (index, value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 1) {
      // 粘贴场景：分发到各格
      const next = Array(GA_LENGTH).fill('');
      digits.split('').forEach((ch, i) => {
        if (i < GA_LENGTH) next[i] = ch;
      });
      setGaCodeArr(next);
      gaInputRefs.current[Math.min(digits.length, GA_LENGTH) - 1]?.focus();
      return;
    }
    setGaCodeArr((prev) => {
      const next = [...prev];
      next[index] = digits;
      return next;
    });
    if (digits && index < GA_LENGTH - 1) {
      gaInputRefs.current[index + 1]?.focus();
    }
  };

  // 退格回退
  const handleGaKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !gaCodeArr[index] && index > 0) {
      e.preventDefault();
      setGaCodeArr((prev) => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
      gaInputRefs.current[index - 1]?.focus();
    }
  };

  // 粘贴整串
  const handleGaPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const digits = text.replace(/\D/g, '');
    if (digits) {
      handleGaInputChange(0, digits);
    }
  };

  // 提交二次验证
  const handleGaSubmit = async () => {
    const gaCode = gaCodeArr.join('');
    if (!/^\d{6}$/.test(gaCode)) {
      message.warning('请输入完整的6位动态验证码');
      return;
    }
    setGaSubmitting(true);
    try {
      const response = await post('/app/auth/login/ga', {
        loginTicket,
        gaCode
      });

      if (response.data?.token) {
        setGaModalVisible(false);
        await handleLoginSuccess(response.data.token);
      }
    } catch (error) {
      console.error('Google验证码校验失败:', error);
      setGaCodeArr(Array(GA_LENGTH).fill(''));
      setTimeout(() => gaInputRefs.current[0]?.focus(), 100);
    } finally {
      setGaSubmitting(false);
    }
  };

  const handleCloseGaModal = () => {
    setGaModalVisible(false);
    setLoginTicket('');
    setGaAccount('');
    setGaCodeArr(Array(GA_LENGTH).fill(''));
    form.resetFields();
  };

  return (
    <div className="login-page login-page--merchant">
      <div className="login-page__decor">
        <div className="login-page__decor-circle login-page__decor-circle--1" />
        <div className="login-page__decor-circle login-page__decor-circle--2" />
        <div className="login-page__decor-geo login-page__decor-geo--tl" />
        <div className="login-page__decor-geo login-page__decor-geo--br" />
        <div className="login-page__decor-wave" />
      </div>

      <div className="login-page__sunset" aria-hidden="true">
        <div className="login-page__sunset-ambient" />
        <div className="login-page__sunset-disc">
          <div className="login-page__sunset-rays" />
          <div className="login-page__sunset-halo" />
          <div className="login-page__sunset-core" />
        </div>
      </div>

      <div className="login-card">
        <div className="login-card__brand login-card__brand--merchant">
          <div className="login-card__kente-strip" />
          <div className="login-card__brand-badge">
            <SafetyCertificateOutlined />
            <span>Africa Merchant</span>
          </div>
          <h1 className="login-card__brand-title">商户管理后台</h1>
          <p className="login-card__brand-slogan">服务非洲商户 · 便捷收款管理</p>
          <ul className="login-card__brand-features">
            <li>非洲本地支付方式接入</li>
            <li>多应用统一管理</li>
            <li>资金流水实时可查</li>
          </ul>
          <div className="login-card__illustration">
            <img src="/images/login-merchant-illustration.svg" alt="" />
          </div>
        </div>

        <div className="login-card__form">
          <div className="login-card__form-header">
            <h2 className="login-card__form-title">商户登录</h2>
            <p className="login-card__form-subtitle">请输入手机号和密码</p>
          </div>

          <Form
            form={form}
            name="merchantLogin"
            onFinish={handleLogin}
            autoComplete="off"
            size="large"
          >
            <Form.Item
              name="phone"
              rules={[
                { required: true, message: '请输入手机号' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="手机号"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6位' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="密码"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                className="login-card__submit login-card__submit--merchant"
              >
                登录
              </Button>
            </Form.Item>

            <div className="login-card__divider">或</div>

            <Button
              block
              htmlType="button"
              className="login-card__back-btn"
              icon={<SafetyCertificateOutlined />}
              onClick={() => navigate('/login')}
            >
              返回管理员登录
            </Button>
          </Form>
        </div>
      </div>

      <footer className="login-page__footer">
        © 2026 商户管理后台系统 · All rights reserved.
      </footer>

      <Modal
        className="login-ga-modal"
        title={null}
        open={gaModalVisible}
        onCancel={handleCloseGaModal}
        footer={null}
        width={420}
        closable={false}
        maskClosable={false}
      >
        <div className="login-ga-modal__body">
          <div className="login-ga-modal__icon login-ga-modal__icon--merchant">
            <SafetyCertificateOutlined />
          </div>
          <h2 className="login-ga-modal__title">二次验证</h2>
          <p className="login-ga-modal__desc">
            {gaAccount ? `账号 ${gaAccount} ` : ''}已开启 Google 验证器，请输入 6 位动态验证码
          </p>

          <div className="login-ga-modal__digits" onPaste={handleGaPaste}>
            {gaCodeArr.map((digit, i) => (
              <Input
                key={i}
                ref={(el) => (gaInputRefs.current[i] = el)}
                className={`login-ga-modal__digit login-ga-modal__digit--merchant${digit ? ' filled' : ''}`}
                value={digit}
                maxLength={6}
                inputMode="numeric"
                onChange={(e) => handleGaInputChange(i, e.target.value)}
                onKeyDown={(e) => handleGaKeyDown(i, e)}
              />
            ))}
          </div>

          <div className="login-ga-modal__actions">
            <Button
              size="large"
              className="login-ga-modal__btn"
              onClick={handleCloseGaModal}
            >
              取消
            </Button>
            <Button
              type="primary"
              size="large"
              className="login-ga-modal__btn login-ga-modal__btn--confirm--merchant"
              loading={gaSubmitting || loading}
              onClick={handleGaSubmit}
            >
              确认
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MerchantLogin;
